import React, { useState } from 'react';
import { Table, Tag, Empty, Alert, Typography } from 'antd';
import { useListAuditLogsQuery } from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const { Text, Paragraph } = Typography;

function JsonBlock({ value, label }) {
  if (value == null) {
    return (
      <div style={{ marginBottom: 8 }}>
        <Text strong>
          {label}
          :
        </Text>
        {' '}
        <Text type="secondary">—</Text>
      </div>
    );
  }
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div style={{ marginBottom: 8 }}>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
      <pre
        style={{
          margin: 0,
          padding: 10,
          borderRadius: 8,
          background: 'var(--df-surface)',
          border: '1px solid var(--df-border)',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 220,
          overflow: 'auto',
        }}
      >
        {text}
      </pre>
    </div>
  );
}

export default function AuditLogsPage() {
  const [page, setPage] = useState({ current: 1, pageSize: 25 });
  const offset = (page.current - 1) * page.pageSize;
  const { data, isLoading, isError, error } = useListAuditLogsQuery({
    limit: page.pageSize,
    offset,
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'When',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (d) => (d ? new Date(d).toLocaleString() : '—'),
    },
    {
      title: 'Actor',
      key: 'actor',
      width: 180,
      ellipsis: true,
      render: (_, row) => row.user_display_name || row.user_email || (row.user_id ? `#${row.user_id}` : 'System'),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag color="blue" style={{ whiteSpace: 'normal', maxWidth: '100%', height: 'auto' }}>
          {action}
        </Tag>
      ),
    },
    {
      title: 'Resource',
      key: 'resource',
      width: 150,
      render: (_, row) => (
        <span>
          {row.resource_type}
          {row.resource_id != null ? ` #${row.resource_id}` : ''}
        </span>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (ip) => ip || '—',
    },
    {
      title: 'UA',
      dataIndex: 'user_agent',
      key: 'user_agent',
      width: 140,
      ellipsis: true,
      render: (t) => t || '—',
    },
    {
      title: 'Trace',
      dataIndex: 'trace_id',
      key: 'trace_id',
      width: 120,
      ellipsis: true,
      render: (t) => t || '—',
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Audit Logs"
        subtitle="Immutable trail of mutating admin and API actions — expand a row for before/after state"
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load audit logs'}
        />
      )}

      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          size="middle"
          scroll={{ x: 1100 }}
          pagination={{
            current: page.current,
            pageSize: page.pageSize,
            total: data?.page?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            onChange: (current, pageSize) => setPage({ current, pageSize }),
          }}
          expandable={{
            expandedRowRender: (row) => (
              <div>
                <Paragraph style={{ marginBottom: 8, fontSize: 12, color: 'var(--df-text-secondary)' }}>
                  Full action:
                  {' '}
                  <Text code>{row.action}</Text>
                </Paragraph>
                <JsonBlock label="Before state" value={row.before_state} />
                <JsonBlock label="After state" value={row.after_state} />
              </div>
            ),
          }}
          locale={{ emptyText: <Empty description="No audit events yet" /> }}
        />
      </div>
    </div>
  );
}
